import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { Project, ProjectCreateRequest, ProjectManagementService } from '../../services/project-management.service';
import { CommonConfirmDialogComponent } from '../common-confirm-dialog/common-confirm-dialog.component';
import { GenericTemplateCommonService } from '../../shared/components/template-ai-common/services/generic-template-common.service';

export interface AddCompetitorDialogData {
    projectName: string;
    projectId: string;
    primaryDomain?: string;
}

@Component({
    selector: 'app-add-competitor-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule, MatIconModule, MatFormFieldModule, MatInputModule],
    templateUrl: './add-competitor-dialog.component.html',
    styleUrl: './add-competitor-dialog.component.scss',
})
export class AddCompetitorDialogComponent implements OnInit {
    competitorName = '';
    competitorUrl = '';
    processing = false;
    private existingCompetitorNames = new Set<string>();
    private existingCompetitorDomains = new Set<string>();

    constructor(
        private dialogRef: MatDialogRef<AddCompetitorDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: AddCompetitorDialogData,
        private snackBar: MatSnackBar,
        private projectService: ProjectManagementService,
        private dialog: MatDialog,
        private router: Router,
        private templateCommonService: GenericTemplateCommonService,
    ) {}

    ngOnInit(): void {
        this.loadExistingCompetitors();
    }

    get canSubmit(): boolean {
        return (
            !!this.competitorName.trim() &&
            !!this.competitorUrl.trim() &&
            !this.isPrimaryBrandConflict &&
            !this.isExistingCompetitorConflict
        );
    }

    get isPrimaryBrandNameConflict(): boolean {
        return this.normalizeText(this.competitorName) === this.normalizeText(this.data.projectName);
    }

    get isPrimaryBrandDomainConflict(): boolean {
        const competitorDomain = this.extractDomain(this.competitorUrl);
        const primaryDomain = this.extractDomain(this.data.primaryDomain || '');
        return !!competitorDomain && !!primaryDomain && competitorDomain === primaryDomain;
    }

    get isPrimaryBrandConflict(): boolean {
        return this.isPrimaryBrandNameConflict || this.isPrimaryBrandDomainConflict;
    }

    get isExistingCompetitorNameConflict(): boolean {
        return this.existingCompetitorNames.has(this.normalizeText(this.competitorName));
    }

    get isExistingCompetitorDomainConflict(): boolean {
        const competitorDomain = this.extractDomain(this.competitorUrl);
        return !!competitorDomain && this.existingCompetitorDomains.has(competitorDomain);
    }

    get isExistingCompetitorConflict(): boolean {
        return this.isExistingCompetitorNameConflict || this.isExistingCompetitorDomainConflict;
    }

    async onReAnalysis(): Promise<void> {
        if (!this.canSubmit || this.processing) return;
        const confirmed = await firstValueFrom(
            this.dialog
                .open(CommonConfirmDialogComponent, {
                    width: '460px',
                    disableClose: true,
                    data: {
                        title: 'Confirm Re-Analysis',
                        description:
                            'This will delete the old analysis version and start a new Re-Analysis with the added competitor. Do you want to continue?',
                    },
                })
                .afterClosed(),
        );
        if (!confirmed) return;

        this.processing = true;
        try {
            const project = await firstValueFrom(this.projectService.getProjectById(this.data.projectId));
            const request = this.buildUpdateRequest(project);

            await firstValueFrom(this.projectService.updateProject(this.data.projectId, request));
            await firstValueFrom(this.projectService.deleteProjectAnalysisVersions(this.data.projectId));

            this.snackBar.open('Competitor updated and old versions deleted.', 'Close', {
                duration: 3000,
            });
            this.templateCommonService.initializeGlobalLoaderForBulkAnalysis();
            this.dialogRef.close(true);
            // Open analysis route fresh so it triggers bulk-search and status/loader flow.
            window.location.href = `/common-ai-analysis/generic/${this.data.projectId}`;
        } catch (err: any) {
            this.snackBar.open(err?.error?.detail || 'Failed to update competitor flow', 'Close', {
                duration: 5000,
            });
        } finally {
            this.processing = false;
        }
    }

    onClose(): void {
        this.dialogRef.close();
    }

    private buildUpdateRequest(project: Project): ProjectCreateRequest {
        const normalizedUrl = this.normalizeCompetitorUrl(this.competitorUrl.trim());
        const existing = (project.competitors || []).filter((c) => c?.name?.trim() && c?.url?.trim());
        const alreadyExists = existing.some(
            (c) =>
                c.name.trim().toLowerCase() === this.competitorName.trim().toLowerCase() ||
                c.url.trim().toLowerCase() === normalizedUrl.toLowerCase(),
        );
        const nextCompetitors = alreadyExists
            ? existing
            : [...existing, { name: this.competitorName.trim(), url: normalizedUrl }];

        return {
            name: project.name,
            description: project.description || '',
            primary_country: project.primary_country || 'Global',
            language: project.language || 'English',
            competitors: nextCompetitors.slice(0, 10),
            industry: project.industry,
            category: project.category,
            industries: project.industries,
            categories: project.categories,
            analysis_categories: project.analysis_categories,
        };
    }

    private normalizeCompetitorUrl(url: string): string {
        if (!url) return url;
        return /^https?:\/\//i.test(url) ? url : `https://${url}`;
    }

    private normalizeText(value: string): string {
        return (value || '').trim().toLowerCase();
    }

    private extractDomain(value: string): string {
        const raw = (value || '').trim().toLowerCase();
        if (!raw) return '';
        try {
            const parsed = new URL(/^https?:\/\//.test(raw) ? raw : `https://${raw}`);
            return parsed.hostname.replace(/^www\./, '');
        } catch {
            return raw
                .replace(/^https?:\/\//, '')
                .replace(/^www\./, '')
                .split('/')[0]
                .split(':')[0]
                .trim();
        }
    }

    private loadExistingCompetitors(): void {
        this.projectService.getProjectById(this.data.projectId).subscribe({
            next: (project) => {
                const competitors = project.competitors || [];
                competitors.forEach((competitor) => {
                    const name = this.normalizeText(competitor?.name || '');
                    const domain = this.extractDomain(competitor?.url || '');
                    if (name) this.existingCompetitorNames.add(name);
                    if (domain) this.existingCompetitorDomains.add(domain);
                });
            },
            error: () => {
                // Keep dialog usable even if this pre-validation fetch fails.
            },
        });
    }
}
